import { useEffect, useRef } from 'react';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const isVisible = (el: HTMLElement) => !el.hasAttribute('disabled') && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

/**
 * Keyboard focus-trap for a custom modal that can't use `ModalShell` (e.g. one needing a higher z-index).
 * While `active`, Tab / Shift+Tab cycle within the returned ref's subtree instead of escaping to the
 * background, and focus is restored to whatever was focused before, on unmount. Modals keep their own
 * focus-on-open + Escape handlers; this just adds the missing trap + restore. Mirrors `ModalShell` /
 * `useFullScreenPanelA11y`.
 */
export function useTabTrap<T extends HTMLElement>(active: boolean) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!active) return undefined;
    const el = ref.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !el) return;
      const f = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);
      if (!f.length) return;
      const first = f[0]!;
      const last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => { document.removeEventListener('keydown', onKeyDown, true); previouslyFocused?.focus?.(); };
  }, [active]);
  return ref;
}
